// b12 Asalari — sweet kids' garden-party birthday preview card.
//
// The full template is a sky-blue + sun-yellow + cream + coral + mint
// birthday invitation with bees, butterflies, balloons, clouds. The
// catalog card keeps that mood: a sky-blue paper-fold frame with
// cloud puffs, an inner cream card, and a focal medallion holding a
// bee + a heart of three balloons over a soft sky.
import { type LayoutCtx } from "./_shared";

const FRAME =
  "linear-gradient(155deg, #7ec5e6 0%, #4ba8d6 55%, #a9e0c3 100%)";

const CLOUDS: Array<[number, number, number]> = [
  [12, 10, 14], [70, 7, 18], [86, 22, 11],
  [6, 32, 13], [60, 88, 16], [22, 90, 11],
];

export function AsalariPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const sunDeep = "#f0a93a";
  const coral = "#ff998a";
  const mint = "#a9e0c3";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      {CLOUDS.map(([x, y, w], i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${x}%`,
            top: `${y}%`,
            width: `${w}%`,
            height: `${w * 0.55}%`,
            background: "#ffffff",
            borderRadius: 999,
            opacity: 0.78,
            boxShadow: "0 4px 8px rgba(30, 60, 110, 0.12)",
          }}
        />
      ))}
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.36em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TUG'ILGAN KUN
        </div>

        {/* sun + balloon trio + bee medallion */}
        <div style={{ width: "62%", aspectRatio: "1 / 1", position: "relative", marginBottom: "5%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `1px solid ${accent}55`,
              overflow: "hidden",
              background: "linear-gradient(180deg, #d6efff 0%, #fff5d4 65%, #ffe6c2 100%)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg viewBox="0 0 100 100" width="92%" height="92%" aria-hidden>
              {/* sun */}
              <circle cx="78" cy="22" r="9" fill={accent} opacity="0.95" />
              {[0, 1, 2, 3, 4, 5].map(i => {
                const a = (i / 6) * Math.PI * 2;
                const x = 78 + Math.cos(a) * 13;
                const y = 22 + Math.sin(a) * 13;
                return <circle key={i} cx={x} cy={y} r="1.2" fill={accent} opacity="0.7" />;
              })}

              {/* cloud */}
              <g opacity="0.85">
                <ellipse cx="22" cy="26" rx="9" ry="4.5" fill="#fff" />
                <ellipse cx="30" cy="23" rx="6" ry="4.5" fill="#fff" />
                <ellipse cx="16" cy="24" rx="5" ry="3.5" fill="#fff" />
              </g>

              {/* three balloons in a heart cluster */}
              <g>
                <ellipse cx="36" cy="60" rx="8.5" ry="10" fill={coral} />
                <polygon points="36,70 34,72 38,72" fill={coral} />
                <ellipse cx="64" cy="60" rx="8.5" ry="10" fill={accent} />
                <polygon points="64,70 62,72 66,72" fill={accent} />
                <ellipse cx="50" cy="68" rx="10" ry="12" fill={mint} />
                <polygon points="50,80 47.5,82 52.5,82" fill={mint} />
                {/* strings */}
                <path d="M36 72 Q 36 82, 50 88" stroke={ink} strokeWidth="0.6" fill="none" opacity="0.5" />
                <path d="M64 72 Q 64 82, 50 88" stroke={ink} strokeWidth="0.6" fill="none" opacity="0.5" />
                <path d="M50 82 L 50 92" stroke={ink} strokeWidth="0.6" opacity="0.5" />
              </g>

              {/* a tiny bee top-left */}
              <g transform="translate(20, 50)">
                <ellipse cx="0" cy="0" rx="3.6" ry="2.4" fill={accent} />
                <rect x="-2.4" y="-2.2" width="1.4" height="4.5" fill="#1d2238" />
                <rect x="0.4" y="-2.2" width="1.4" height="4.5" fill="#1d2238" />
                <ellipse cx="-1.8" cy="-2.6" rx="2.2" ry="1.4" fill="#fff" opacity="0.85" />
                <ellipse cx="1.8" cy="-2.6" rx="2.2" ry="1.4" fill="#fff" opacity="0.85" />
              </g>
            </svg>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 30, lineHeight: 1.06, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.3em",
            color: sunDeep,
            marginTop: "5%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
